<template>
  <tabs class="!py-5">
    <template #actions>
      <Switch
        v-model="hideDeleted"
        :label="$t('hideDeletedDrivers')"
      />
    </template>
  </tabs>
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filterParams" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/company_payouts/csv"
      :filter="providerParams.filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    >
      <div class="mt-16 text-red text-body-big">
        {{ $t('toolbar.export.downloadPainFile.text') }}
      </div>
      <Multiselect
        v-model="painFileAccount"
        :options="painFileOptions"
        :label="$t('toolbar.export.downloadPainFile.dropdown.DEFAULT')"
        :can-deselect="false"
        :can-clear="false"
        class="mt-3 mb-5"
      />
      <ExportLink
        v-if="painFileAccount && checkedIdsList.length > 0"
        :custom-export="exportPainFile"
        :label="$t('toolbar.export.downloadPainFile.button')"
        class="mt-5"
      />
      <div v-else-if="checkedIdsList.length === 0">
        {{ $t('toolbar.export.downloadPainFile.noPayoutsSelected') }}
      </div>
    </export>
  </toolbar>
  <edit-company-payout-modal
    v-model:open="openEditCompanyPayoutModal"
    :edit-payout="editCompanyPayout"
    @saved="refetchStatistics"
  />
  <div>
    <div class="ml-12 flex flex-wrap gap-x-10 gap-y-10 pb-10 pt-5">
      <CompanyPayoutStatisticsTable
        :statistics="{
          total: filteredStatistics?.total ?? {},
          filtered: filteredStatistics?.filtered ?? {},
          selected: selectedPayoutStatistics,
        }"
        :loading="loadingStatistics"
        :admins-are-allowed="true"
      />
    </div>
    <data-table
      class="mb-auto ml-12"
      v-model:sort-by="sortKey"
      v-model:sort-direction="sortDirection"
      :busy="loading"
      :items="records"
      :columns="columns"
      :visible-columns="visibleColumns"
      :disableFrontendSorting="true"
      :choosable="true"
      :actions="actions"
      v-model:checked-ids-list="checkedIdsList"
    />
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import {computed, inject, reactive, ref, unref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import FilterPane from '@components/utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@components/utils/toolbar/panes/AddFields.vue';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Pagination from '@components/utils/table/Pagination.vue';
import DataTable, {type Column} from '@components/utils/table/Table.vue';
import {
    enumOptionsProvider,
    useProvider,
} from '@components/utils/table/hooks/provider';
import {format} from 'date-fns';
import type {Filter, ProviderParams} from '@/components/utils/table/hooks/provider';
import {
  License,
  type Company,
  type CompanyPayout,
  type Download,
  type HydraCollection,
  type Tour
} from '@/entities/Entities';
import Tabs from '@components/structure/navigation/Tabs.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import ExportLink from '@utils/toolbar/panes/ExportLink.vue';
import Multiselect from '@components/utils/Multiselect.vue';
import {useQueryRef} from '@/hooks/url';
import Switch from '@utils/inputs/Switch.vue';
import {useTableFields} from '@hooks/table-configuration';
import { useRoute } from 'vue-router';
import globalLicenseFilterKey from '@/components/utils/globalLicenseFilter/injects';
import { calculateSelectedCompanyPayoutStatistics, StatisticsItem } from '@/hooks/company-payout';
import CompanyPayoutStatisticsTable from '@/components/utils/CompanyPayoutStatisticsTable.vue';
import EditCompanyPayoutModal from './EditCompanyPayoutModal.vue';
import { fetchApi, FetchError, useFetchAction } from '@/hooks/fetch';
import { useDownloads } from '@/hooks/tasks';
import { useConfirmPrompt } from '@/components/utils/prompt/injects';
import { useNotify } from '@/components/utils/notifications';

interface Props {
  modelValue: Company | null;
}

const props = defineProps<Props>();

const hideDeleted = ref(true);

const route = useRoute();

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef('filter', {});

const sortKey = useQueryRef<string | undefined>('sortKey', 'createdAt');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const openEditCompanyPayoutModal = ref(false);
const editCompanyPayout = ref<CompanyPayout>();

const { t } = useI18n();

const globalFilter = inject(globalLicenseFilterKey)!

const filterLicense = ref<License>();
watch(() => globalFilter.value.licenseId, async () => {
  if(!globalFilter.value.licenseId) {
    filterLicense.value = undefined;
    return;
  }

  filterLicense.value = await fetchApi<License>(globalFilter.value.licenseId);
}, {immediate: true});
const csvTitle = computed(() => {
  let title = t('csv.title.companyPayoutsFromCompany', {
    company: props.modelValue?.name,
  });

  if(filterLicense.value) {
    title += `_${filterLicense.value?.startDate ? format(new Date(filterLicense.value.startDate), 'dd-MM-yyyy') : ''}_${filterLicense.value?.endDate ? format(new Date(filterLicense.value.endDate), 'dd-MM-yyyy') : ''}`;
  }

  if(globalFilter.value?.boxType) {
    title += `_${t('licenseSeatRegistration.licenseSeat.type.'+globalFilter.value.boxType)}`;
  }

  return title;
});

const sortParams = reactive({
  sortKey,
  sortDirection,
})
const filterParams = ref<Filter>(filter.value)
const paginationParams = reactive({
  page,
  perPage,
})
const providerParams = computed<ProviderParams>(() => {
  const filter: Filter = {
    ...filterParams.value,
    'licenseSeatRegistration.licenseSeat.license.company': `/companies/${route.params.companyId}`,
  }

  if(globalFilter.value?.licenseId) {
    filter['licenseSeatRegistration.licenseSeat.license'] = globalFilter.value.licenseId
  }

  if(globalFilter.value?.boxType) {
    filter['licenseSeatRegistration.licenseSeat.type'] = globalFilter.value.boxType
  }

  if (hideDeleted.value) {
    filter['driver.user.deleted'] = false
  }
  return {
    sort: sortParams,
    filter,
    pagination: paginationParams,
  }
});

const actions = [
{
    title: t('pages.companyPayoutList.actions.edit'),
    action: (item: CompanyPayout) => {
      editCompanyPayout.value = item;
      openEditCompanyPayoutModal.value = true;
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.companyPayoutList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    sortable: true,
  },
  {
    key: 'executionTimestamp',
    title: t('payout.executionDate'),
    group: t('pages.companyPayoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.companyPayoutList.fieldGroups.general'),
      options: {
        label: t('payout.executionDate'),
      },
    },
  },
  {
    key: 'paidDate',
    title: t('payout.paidDate'),
    group: t('pages.companyPayoutList.fieldGroups.dates'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.companyPayoutList.filterGroups.dates'),
      options: {
        label: t('payout.paidDate'),
      },
    },
  },
  {
    key: 'createdAt',
    title: t('createdAt'),
    group: t('pages.companyPayoutList.fieldGroups.dates'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.companyPayoutList.filterGroups.dates'),
      options: {
        label: t('createdAt'),
      },
    },
  },
  {
    key: 'updatedAt',
    title: t('updatedAt'),
    group: t('pages.companyPayoutList.fieldGroups.dates'),
    sortable: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.companyPayoutList.filterGroups.dates'),
      options: {
        label: t('updatedAt'),
      },
    },
  },
  {
    key: 'driver.user.firstName',
    title: t('user.firstName'),
    sortable: true,
    visible: true,
    group: t('pages.companyPayoutList.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.companyPayoutList.filterGroups.general'),
      options: {
        label: t('user.firstName'),
      },
    },
  },
  {
    key: 'driver.user.lastName',
    title: t('user.lastName'),
    group: t('pages.companyPayoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.companyPayoutList.filterGroups.general'),
      options: {
        label: t('user.lastName'),
      },
    },
  },
  {
    key: 'driver.canton',
    title: t('driver.canton'),
    group: t('pages.companyPayoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => t(`canton.${v}`),
    filter: {
      type: 'dropdown',
      group: t('pages.companyPayoutList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/Canton', (v) => ({
          value: v,
          label: v ? t(`canton.${v}`) : '-',
        })),
        label: t('driver.canton'),
      },
    },
  },
  {
    key: 'status',
    csvTranslations: {
      pending: t('payoutStatus.pending'),
      inPayment: t('payoutStatus.inPayment'),
      payed: t('payoutStatus.payed'),
      error: t('payoutStatus.error'),
    },
    title: t('payout.status'),
    group: t('pages.companyPayoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (item: string) => t(`payoutStatus.${item}`),
    filter: {
      type: 'dropdown',
      group: t('pages.companyPayoutList.fieldGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/PayoutStatus', (item) => ({
          value: item,
          label: t(`payoutStatus.${item}`),
        })),
        label: t('payout.status'),
      },
    },
  },
  {
    key: 'includeIntoBankDocument',
    title: t('payout.includeIntoBankDocument'),
    group: t('pages.companyPayoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: boolean) => (v ? t('yes') : t('no')),
    filter: {
      type: 'bool',
      group: t('pages.companyPayoutList.filterGroups.general'),
      options: {
        label: t('payout.includeIntoBankDocument'),
      },
    },
  },
  {
    key: 'comment',
    title: t('payout.comment'),
    group: t('pages.companyPayoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.companyPayoutList.filterGroups.general'),
      options: {
        label: t('payout.comment'),
      },
    },
  },
  {
    key: 'rewardChf',
    title: t('licenses.reward.amount_chf'),
    group: t('pages.companyPayoutList.fieldGroups.rewards'),
    sortable: true,
    visible: true,
    unit: 'CHF',
    computed: (payout: CompanyPayout) => formatPayout(payout, 'chf'),
    filter: {
      type: 'range',
      group: t('pages.companyPayoutList.filterGroups.rewards'),
      options: {
        icon: <span>CHF</span>,
        label: t('licenses.reward.amount_chf'),
      },
    },
  },
  {
    key: 'rewardHoliday',
    title: t('licenses.reward.amount_holiday'),
    group: t('pages.companyPayoutList.fieldGroups.rewards'),
    sortable: true,
    visible: true,
    unit: 'Tage',
    computed: (payout: CompanyPayout) => formatPayout(payout, 'holiday'),
    filter: {
      type: 'range',
      group: t('pages.companyPayoutList.filterGroups.rewards'),
      options: {
        icon: <span>Tage</span>,
        label: t('licenses.reward.amount_holiday'),
      },
    },
  },
  {
    key: 'rewardVoucher',
    title: t('licenses.reward.amount_voucher'),
    group: t('pages.companyPayoutList.fieldGroups.rewards'),
    sortable: true,
    visible: true,
    unit: 'CHF',
    computed: (payout: CompanyPayout) => formatPayout(payout, 'voucher'),
    filter: {
      type: 'range',
      group: t('pages.companyPayoutList.filterGroups.rewards'),
      options: {
        icon: <span>CHF</span>,
        label: t('licenses.reward.amount_voucher'),
      },
    },
  },
  {
    key: 'payoutPercentage',
    title: t('licenses.reward.percentage'),
    group: t('pages.companyPayoutList.fieldGroups.rewards'),
    sortable: true,
    visible: true,
    unit: '%',
    formatter: (v: number) => `${v.toFixed(2).padStart(5, '0')}%`,
    filter: {
      type: 'range',
      group: t('pages.companyPayoutList.filterGroups.rewards'),
      options: {
        icon: <span>%</span>,
        label: t('licenses.reward.percentage'),
      },
    },
  },
  {
    key: 'licenseSeatRegistration.licenseSeat.license',
    title: t('driver.license'),
    sortable: false,
    visible: true,
    group: t('pages.driverList.fieldGroups.license'),
    computed: (companyPayout: CompanyPayout) =>
      `${companyPayout.company?.name} - ${companyPayout?.licenseSeatRegistration?.licenseSeat?.license?.startDate ?
        format(new Date(companyPayout.licenseSeatRegistration?.licenseSeat?.license?.startDate),
          'dd.MM.yyyy') : ''} -${companyPayout?.licenseSeatRegistration?.licenseSeat?.license?.endDate ?
        format(new Date(companyPayout.licenseSeatRegistration?.licenseSeat?.license?.endDate), 'dd.MM.yyyy') : ''}`,
  },
  {
    key: 'licenseSeatRegistration.licenseSeat.type',
    title: t('pages.companyPayoutList.fields.licenseType'),
    group: t('pages.companyPayoutList.fieldGroups.license'),
    sortable: true,
    visible: true,
    formatter: (v: string) => t(`licenses.boxType.${v}`),
    csvTranslations: {
      box: t('licenses.boxType.box'),
      noBox: t('licenses.boxType.noBox'),
    },
    filter: {
      type: 'dropdown',
      group: t('pages.companyPayoutList.fieldGroups.license'),
      options: {
        options: enumOptionsProvider('/enums/LicenseType', (v) => ({
          value: v,
          label: t(`licenses.boxType.${v}`),
        })),
        label: t('pages.companyPayoutList.fields.licenseType'),
      },
    },
  },
  {
    key: 'licenseSeatRegistration.licenseSeat.license.startDate',
    title: t('licenses.startDate'),
    group: t('pages.driverList.fieldGroups.license'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.license'),
      options: {
        label: t('licenses.startDate'),
      },
    },
  },
  {
    key: 'licenseSeatRegistration.licenseSeat.license.endDate',
    title: t('licenses.endDate'),
    group: t('pages.driverList.fieldGroups.license'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.license'),
      options: {
        label: t('licenses.endDate'),
      },
    },
  },
];

const formatPayout = (payout: CompanyPayout, type: 'chf' | 'holiday' | 'voucher' | 'percentage') => {
  const amount = payout?.entries
  ?.filter((entry) => entry.type === type)
  ?.map((entry) => entry.amount ?? 0)
  ?.reduce((partialSum, x) => partialSum + x, 0) ?? 0;

  if( type === 'chf' || type === 'voucher') {
    return amount.toFixed(2).padStart(5, '0') + ' CHF';
  } else if( type === 'holiday' ) {
    return amount.toFixed(2).padStart(5, '0') + ' Tage';
  } else {
    return '-';
  }
}


const visibleColumns = useTableFields(
    columns,
);

export interface Statistics {
  filtered: StatisticsItem;
  selected?: StatisticsItem;
  total: StatisticsItem;
}

const {
  data: filteredStatistics,
  loading: loadingStatistics,
  refetch: refetchStatistics,
} = useProvider<Statistics>('/company_payouts/payout-statistics', providerParams);

const checkedIdsList = ref<string[]>([]);

const checkedPayouts = computed(
  () => records.value?.filter((payout: CompanyPayout) => checkedIdsList.value.includes(payout.id)) ?? []
);

const selectedPayoutStatistics = calculateSelectedCompanyPayoutStatistics(checkedPayouts);

const { data: items, loading, refetch: refetchItems } = useProvider<HydraCollection<CompanyPayout>>('/company_payouts', providerParams);

const refetch = () => {
  refetchItems();
  refetchStatistics();
};

const notify = useNotify();

const [exec, { loading: loadingPainFile }] = useFetchAction();
const downloads = useDownloads();

const confirm = useConfirmPrompt();

const painFileAccount = ref();
const painFileOptions = enumOptionsProvider('/enums/WBBankAccounts', {}, (item) => ({
  label: t(`toolbar.export.downloadPainFile.dropdown.${item}`),
  value: item,
}));

const exportPainFile = async (ignoreAlreadyExported: boolean = false) => {
  if (!painFileAccount.value) {
    return;
  }
  try {
    const download = await exec<Download>(`${props.modelValue?.['@id']}/pain`, {
      method: 'POST',
      body: {
        ignoreAlreadyExported,
        account: painFileAccount.value,
        payoutIds: checkedIdsList.value,
      },
    });
    downloads?.addTask(download);
    notify?.({
      variant: 'success',
      title: t('toolbar.export.successRequest'),
    });
    refetch();
  } catch (e) {
    if (e instanceof FetchError) {
      const errorCode = await e.response.json();
      if (errorCode?.error?.key === 'no_payouts') {
        notify?.({
          variant: 'info',
          title: t('painExport.noPayouts'),
        });
        return;
      } else if (errorCode?.error?.key === 'already_exported' && !ignoreAlreadyExported) {
        const answer = await confirm?.(t('painExport.alreadyExportedConfirmation'));
        if (answer) {
          await exportPainFile(true);
        }
        return;
      }
    }
    notify?.({
      variant: 'error',
      title: t('toolbar.export.errorRequest'),
    });
  }
};

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
